/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import BaseEditor, { ActionComposerEditorArgs, FieldName } from 'cio/components/action-composer/base-glimmer/editor';
import TemplateModel from 'cio/models/template';
import { isBlank, isEmpty, isNone } from '@ember/utils';
import { action, computed } from '@ember/object';
import afterRender from 'cio/utils/after-render';
import { DEFAULT_RICH_PUSH_BODY_JSON } from 'cio/utils/push/rich-push';
import { Editor } from 'cio/services/editor';
import { inject as service } from '@ember/service';
import AccountService from 'cio/services/account';
import { tracked } from '@glimmer/tracking';
import { getLiquidToInsert } from 'cio/utils/liquid';

type Recipient = {
  value: string | null;
  label: string;
};

const PUSH_MESSAGE_IMAGE_BYTES_LIMIT = 1 * 1024 * 1024; // 1MB

interface RichPushEditorSignature {
  Args: ActionComposerEditorArgs;
  Element: HTMLDivElement;
}

export default class RichEditor<
  Signature extends RichPushEditorSignature = RichPushEditorSignature,
> extends BaseEditor<Signature> {
  @service account!: AccountService;

  @tracked showFeedbackModal = false;
  imageSizeLimitInBytes = PUSH_MESSAGE_IMAGE_BYTES_LIMIT;
  customDataPreviewImage = new URL('cio/assets/images/push/custom-data-preview.png', import.meta.url).toString();

  recipientItems: readonly Recipient[] = Object.freeze([
    { value: null, label: 'All customer devices' },
    { value: 'last_used', label: 'Last used device' },
    { value: 'customized', label: 'Custom device token' },
  ]);
  isCustomizingRecipient = false;
  toField!: HTMLInputElement;

  readonly editorClassNames = {
    'template.to': 'js-editor-field-to',
    'template.subject': 'js-editor-field-subject',
    'template.body': 'js-editor-textarea',
    'template.imageUrl': 'js-editor-field-image-url',
    'template.bodyJson.link': 'js-editor-field-deep-link',
    'template.bodyJson.customData': 'js-editor-field-custom-data',
  };
  readonly defaultRecipients = ['all', 'last_used'];

  get sound() {
    let soundValue = this.payload?.sound;
    return soundValue != null && soundValue.trim() !== '' ? soundValue : 'none';
  }

  @action
  initializeTemplate() {
    if (isEmpty(this.template.bodyJson)) {
      this.template.bodyJson = DEFAULT_RICH_PUSH_BODY_JSON;
    }

    if (this.template.editor !== Editor.JSON) {
      this.template.editor = Editor.RICH;
    }
  }

  @action
  onImageFileUrlChange(imageUrl: string) {
    let { template } = this.args.editableEntity;
    if (template != null) {
      template.imageUrl = imageUrl;
    }
  }

  @action
  toggleSound(sound: string) {
    this.template.bodyJson = JSON.stringify({ ...this.payload, sound: sound === 'none' ? '' : sound });
  }

  @computed('args.editableEntity.template.id')
  get template() {
    return this.args.editableEntity.template as TemplateModel;
  }

  @computed('args.editableEntity.template.bodyJson')
  get payload() {
    let { bodyJson } = this.template;
    if (bodyJson == null || bodyJson === '') {
      return null;
    }

    return JSON.parse(bodyJson);
  }

  get isCustomizedRecipient() {
    let { recipient } = this.template;
    return this.isCustomizingRecipient || (!isNone(recipient) && !this.defaultRecipients.includes(<string>recipient));
  }

  get selectedRecipient() {
    let { recipient } = this.template;
    let selectedValue = null;
    if (recipient === 'last_used') {
      selectedValue = recipient;
    } else if (!isBlank(recipient) && recipient !== 'all') {
      selectedValue = 'customized';
    }
    return selectedValue;
  }

  @action
  insertLiquid([fieldName]: [FieldName, number], liquid: string, fallback?: string) {
    let className = this.editorClassNames[fieldName];
    if (!className) {
      return;
    }

    let el = document.querySelector(`.${className}`) as HTMLInputElement | HTMLTextAreaElement;
    let { selectionStart, selectionEnd } = el;

    let replacement = getLiquidToInsert(liquid, fallback, this.template.templateEngine);

    el.setRangeText(replacement, selectionStart ?? 0, selectionEnd ?? 0, 'end');
    if (el !== document.activeElement) {
      el.focus();
    }
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }

  @action
  handleToFieldInsert(element: HTMLInputElement) {
    this.toField = element;
  }

  @action
  onRecipientSelected(selected: Recipient) {
    let { value } = selected;
    if (value === 'customized') {
      this.customizeRecipient();
    } else {
      this.useRecipient(value);
    }
  }

  @action
  onDeepLinkChange(event: InputEvent) {
    let { value } = event.target as HTMLInputElement;
    this.template.bodyJson = JSON.stringify({ ...this.payload, link: value });
  }

  @action
  onCustomDataChange() {
    this.template.bodyJson = JSON.stringify(this.payload);
  }

  customizeRecipient() {
    this.template.set('recipient', '');
    this.isCustomizingRecipient = true;
    afterRender(() => {
      let input = this.toField.querySelector('.fly-form-control') as HTMLInputElement;
      if (input != null) {
        input.focus();
      }
    });
  }

  useRecipient(value: string | null) {
    this.template.set('recipient', value);
    this.isCustomizingRecipient = false;
  }
}
